import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { DndContext, DragEndEvent, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input,
  Label,
} from '@artstorefronts/ui';
import { GripVertical, Loader2, Plus, Trash2 } from 'lucide-react';
import { MediaDropRegion, profileImageUploader, type Uploader, type UploadItem } from '@arthelper/ui';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';

function extractPlatformFeedCardUploadKey(presignedUrl: string): string {
  const pathname = new URL(presignedUrl).pathname;
  const marker = 'platform-feed-card/uploads/';
  const markerIndex = pathname.indexOf(marker);
  if (markerIndex >= 0) {
    return pathname.slice(markerIndex);
  }

  return pathname.startsWith('/') ? pathname.slice(1) : pathname;
}

const ADMIN_PLATFORM_FEED_CARDS = gql`
  query AdminPlatformFeedCards {
    adminPlatformFeedCards {
      cards {
        id
        imageUrl
        destinationUrl
        altText
        commentsEnabled
        isHidden
        sortOrder
        commentCount
      }
      settings {
        cadence
      }
    }
  }
`;

const ADMIN_SAVE_PLATFORM_FEED_CARDS = gql`
  mutation AdminSavePlatformFeedCards($input: AdminSavePlatformFeedCardsInput!) {
    adminSavePlatformFeedCards(input: $input) {
      cards {
        id
        imageUrl
        destinationUrl
        altText
        commentsEnabled
        isHidden
        sortOrder
      }
    }
  }
`;

const ADMIN_DELETE_PLATFORM_FEED_CARD = gql`
  mutation AdminDeletePlatformFeedCard($id: ID!) {
    adminDeletePlatformFeedCard(id: $id)
  }
`;

const ADMIN_UPLOAD_PLATFORM_FEED_CARD_IMAGE = gql`
  mutation AdminUploadPlatformFeedCardImage($uploadId: ID!, $contentType: String) {
    adminUploadPlatformFeedCardImage(uploadId: $uploadId, contentType: $contentType) {
      success
      url
      error
    }
  }
`;

const ADMIN_PROCESS_PLATFORM_FEED_CARD_IMAGE = gql`
  mutation AdminProcessPlatformFeedCardImage($uploadId: ID!, $key: String!) {
    adminProcessPlatformFeedCardImage(uploadId: $uploadId, key: $key) {
      success
      url
      error
    }
  }
`;

interface ServerCard {
  id: string;
  imageUrl: string | null;
  destinationUrl: string | null;
  altText: string | null;
  commentsEnabled: boolean;
  isHidden: boolean;
  sortOrder: number;
  commentCount: number;
}

interface EditorCard {
  clientId: string;
  id?: string;
  imageUrl: string;
  destinationUrl: string;
  altText: string;
  commentsEnabled: boolean;
  isHidden: boolean;
}

function createEmptyCard(): EditorCard {
  return {
    clientId: `new-${crypto.randomUUID()}`,
    imageUrl: '',
    destinationUrl: '',
    altText: '',
    commentsEnabled: false,
    isHidden: false,
  };
}

function mapServerCard(card: ServerCard): EditorCard {
  return {
    clientId: card.id,
    id: card.id,
    imageUrl: card.imageUrl ?? '',
    destinationUrl: card.destinationUrl ?? '',
    altText: card.altText ?? '',
    commentsEnabled: card.commentsEnabled,
    isHidden: card.isHidden,
  };
}

function cardsEqual(a: EditorCard[], b: EditorCard[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((card, index) => {
    const other = b[index];
    return (
      card.clientId === other.clientId &&
      card.id === other.id &&
      card.imageUrl === other.imageUrl &&
      card.destinationUrl === other.destinationUrl &&
      card.altText === other.altText &&
      card.commentsEnabled === other.commentsEnabled &&
      card.isHidden === other.isHidden
    );
  });
}

function CardDragPreview({ card }: { card: EditorCard }) {
  return (
    <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-lg ring-1 ring-black/5">
      <div className="flex items-center gap-3">
        <GripVertical className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-600">{card.destinationUrl || 'New card'}</span>
      </div>
    </div>
  );
}

function FeedCardArtworkField({
  imageUrl,
  onImageUploaded,
  uploader,
}: {
  imageUrl: string;
  onImageUploaded: (url: string | null) => void;
  uploader: Uploader;
}) {
  const existing = useMemo(
    () =>
      imageUrl ? [{ id: `feed-card:${imageUrl}`, url: imageUrl, kind: 'image' as const, name: 'Card artwork' }] : [],
    [imageUrl]
  );
  return (
    <div className="w-full max-w-[200px]">
      <Label className="mb-2 block text-sm">Card artwork</Label>
      {imageUrl && <img src={imageUrl} alt="Card artwork" className="mb-2 w-full rounded-lg border object-cover" />}
      <MediaDropRegion
        width="100%"
        tone="neutral"
        prompt="Upload image"
        hint="JPG, PNG, WebP, GIF · 10 MB"
        accept={['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']}
        maxSizeMB={10}
        multiple={false}
        existing={existing}
        uploader={uploader}
        onItemComplete={(item: UploadItem) => {
          onImageUploaded(item.result as string);
          toast.success('Image uploaded.');
        }}
        onItemError={(_, error) => toast.error(error.message || 'Image upload failed.')}
        onRemove={(item: UploadItem) => {
          if (item.phase === 'done') onImageUploaded(null);
        }}
      />
    </div>
  );
}

function SortableFeedCardRow({
  card,
  isDeleting,
  onUpdate,
  onDelete,
  imageUploader,
}: {
  card: EditorCard;
  isDeleting: boolean;
  onUpdate: (clientId: string, updates: Partial<EditorCard>) => void;
  onDelete: (clientId: string) => Promise<boolean>;
  imageUploader: Uploader;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.clientId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  async function handleConfirmDelete() {
    const deleted = await onDelete(card.clientId);
    if (deleted) {
      setConfirmDelete(false);
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border border-gray-200 bg-white p-4 ${card.isHidden ? 'opacity-60' : ''}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div
          {...attributes}
          {...listeners}
          className="flex shrink-0 cursor-grab touch-none items-center justify-center rounded pt-2 active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>

        <FeedCardArtworkField
          imageUrl={card.imageUrl}
          onImageUploaded={(url) => onUpdate(card.clientId, { imageUrl: url ?? '' })}
          uploader={imageUploader}
        />

        <div className="grid min-w-0 flex-1 gap-3">
          <div>
            <Label htmlFor={`destination-${card.clientId}`} className="mb-1 block text-sm">
              Destination URL
            </Label>
            <Input
              id={`destination-${card.clientId}`}
              value={card.destinationUrl}
              onChange={(e) => onUpdate(card.clientId, { destinationUrl: e.target.value })}
              placeholder="https://"
            />
          </div>
          <div>
            <Label htmlFor={`alt-${card.clientId}`} className="mb-1 block text-sm">
              Alt text (optional)
            </Label>
            <Input
              id={`alt-${card.clientId}`}
              value={card.altText}
              onChange={(e) => onUpdate(card.clientId, { altText: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Label htmlFor={`comments-${card.clientId}`} className="flex items-center gap-2 text-sm font-normal">
              <Checkbox
                id={`comments-${card.clientId}`}
                checked={card.commentsEnabled}
                onCheckedChange={(checked) => onUpdate(card.clientId, { commentsEnabled: checked === true })}
              />
              Allow comments
            </Label>
            <Label htmlFor={`hidden-${card.clientId}`} className="flex items-center gap-2 text-sm font-normal">
              <Checkbox
                id={`hidden-${card.clientId}`}
                checked={card.isHidden}
                onCheckedChange={(checked) => onUpdate(card.clientId, { isHidden: checked === true })}
              />
              Hidden from feed
            </Label>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => setConfirmDelete(true)}
          aria-label="Delete card"
          className="shrink-0 text-gray-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this card?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AdminFeedCardsPage() {
  const [cards, setCards] = useState<EditorCard[]>([]);
  const [savedCards, setSavedCards] = useState<EditorCard[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDirtyRef = useRef(false);

  const { data, loading } = useQuery(ADMIN_PLATFORM_FEED_CARDS, { fetchPolicy: 'cache-and-network' });
  const [saveCards, { loading: saving }] = useMutation(ADMIN_SAVE_PLATFORM_FEED_CARDS);
  const [deleteCard] = useMutation(ADMIN_DELETE_PLATFORM_FEED_CARD);
  const [uploadImage] = useMutation(ADMIN_UPLOAD_PLATFORM_FEED_CARD_IMAGE);
  const [processImage] = useMutation(ADMIN_PROCESS_PLATFORM_FEED_CARD_IMAGE);

  useEffect(() => {
    if (!data?.adminPlatformFeedCards || isDirtyRef.current) {
      return;
    }

    const mapped = data.adminPlatformFeedCards.cards.map(mapServerCard);
    setCards(mapped);
    setSavedCards(mapped);
  }, [data]);

  const cadence = data?.adminPlatformFeedCards?.settings.cadence;

  const hasUnsavedChanges = useMemo(() => {
    return !cardsEqual(cards, savedCards);
  }, [cards, savedCards]);

  useEffect(() => {
    isDirtyRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const activeDragCard = useMemo(() => {
    if (!activeId) {
      return null;
    }
    return cards.find((card) => card.clientId === activeId) ?? null;
  }, [activeId, cards]);

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    setCards((items) => {
      const oldIndex = items.findIndex((item) => item.clientId === active.id);
      const newIndex = items.findIndex((item) => item.clientId === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleUpdateCard = (clientId: string, updates: Partial<EditorCard>) => {
    setCards((prev) => prev.map((card) => (card.clientId === clientId ? { ...card, ...updates } : card)));
  };

  const handleAddCard = () => {
    setCards((prev) => [...prev, createEmptyCard()]);
  };

  const handleDeleteCard = async (clientId: string): Promise<boolean> => {
    const card = cards.find((item) => item.clientId === clientId);
    if (!card) {
      return false;
    }

    if (!card.id) {
      setCards((prev) => prev.filter((item) => item.clientId !== clientId));
      return true;
    }

    setIsDeleting(true);
    try {
      await deleteCard({ variables: { id: card.id } });
      setCards((prev) => prev.filter((item) => item.clientId !== clientId));
      setSavedCards((prev) => prev.filter((item) => item.clientId !== clientId));
      toast.success('Card deleted.');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete card.';
      toast.error(message);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const imageUploader = useMemo(
    () =>
      profileImageUploader({
        presign: async (file) => {
          const uploadId = crypto.randomUUID();
          const signed = (await uploadImage({ variables: { uploadId, contentType: file.type } })).data
            ?.adminUploadPlatformFeedCardImage;
          if (!signed?.url) throw new Error(signed?.error || 'Failed to get upload URL');
          return {
            url: signed.url,
            key: JSON.stringify({ uploadId, key: extractPlatformFeedCardUploadKey(signed.url) }),
          };
        },
        process: async (encoded) => {
          const { uploadId, key } = JSON.parse(encoded) as { uploadId: string; key: string };
          const processed = (await processImage({ variables: { uploadId, key } })).data
            ?.adminProcessPlatformFeedCardImage;
          if (!processed?.url) throw new Error(processed?.error || 'Failed to process image');
          return processed.url as string;
        },
      }),
    [processImage, uploadImage]
  );

  const handleSave = async () => {
    try {
      const result = await saveCards({
        variables: {
          input: {
            cards: cards.map((card, index) => ({
              ...(card.id ? { id: card.id } : {}),
              imageUrl: card.imageUrl || null,
              destinationUrl: card.destinationUrl || null,
              altText: card.altText || null,
              commentsEnabled: card.commentsEnabled,
              isHidden: card.isHidden,
              sortOrder: index,
            })),
          },
        },
      });

      const saved = result.data?.adminSavePlatformFeedCards;
      if (!saved) {
        throw new Error('Save failed');
      }

      const mapped = saved.cards.map(mapServerCard);
      setCards(mapped);
      setSavedCards(mapped);
      toast.success('Feed cards saved.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save feed cards.';
      toast.error(message);
    }
  };

  if (loading && !data) {
    return (
      <AdminLayout
        title="Platform feed cards"
        description="Manage the platform card stable. Card images should be at least 1024 pixels wide (for example, 1024×1280 for a 4:5 card)."
      >
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Platform feed cards"
      description="Manage the platform card stable. Card images should be at least 1024 pixels wide (for example, 1024×1280 for a 4:5 card)."
      action={
        <Button onClick={handleAddCard} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add card
        </Button>
      }
    >
      <div className="space-y-6 pb-24">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-900">Shown every {cadence ?? '…'} posts</p>
          <p className="mt-1 text-xs text-gray-500">
            Applies to all community feed tabs (trending, new, following, first posts). Spacing is part of the live feed
            strategy —{' '}
            <Link to="/admin/feed-strategies" className="font-medium text-gray-900 underline">
              change it in Feed Strategies
            </Link>
            .
          </p>
        </div>

        <DndContext
          collisionDetection={closestCenter}
          onDragStart={(event) => setActiveId(String(event.active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={cards.map((card) => card.clientId)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {cards.map((card) => (
                <SortableFeedCardRow
                  key={card.clientId}
                  card={card}
                  isDeleting={isDeleting}
                  onUpdate={handleUpdateCard}
                  onDelete={handleDeleteCard}
                  imageUploader={imageUploader}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>{activeDragCard ? <CardDragPreview card={activeDragCard} /> : null}</DragOverlay>
        </DndContext>

        {cards.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            No cards yet. Add a card to get started.
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4">
          <p className="text-sm text-gray-600">
            {hasUnsavedChanges ? 'Unsaved changes · cadence, order, and card fields' : 'All changes saved'}
          </p>
          <Button onClick={handleSave} disabled={saving || isDeleting || !hasUnsavedChanges}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminFeedCardsPage;
